<script setup lang="ts">
const props = defineProps<{
  loading?: boolean
  skeleton?: boolean
}>()

const emit = defineEmits<{
  (event: 'click'): void
}>()

const { t } = useI18n()
const activeOrderStore = useActiveOrderStore()
const { loading } = toRefs(props)

const productStore = useProductStore()

const extraLabels = computed(() => {
  const labels = [{
    icon: 'i-lucide:box',
    text: t('product.in_stock'),
  }]

  if (productStore.discountPercentage > 0) {
    labels.push({
      icon: 'i-ri:discount-percent-line',
      text: t('general.discount', [`${productStore.discountPercentage}%`]),
    })
  }

  return labels
})
</script>

<template>
  <div class="flex flex-row flex-nowrap items-center justify-start">
    <div class="wfull flex flex-1 flex-row flex-nowrap items-center justify-start">
      <div
        v-if="activeOrderStore.activeOrder.status === 'pending' || loading" class="flex-1"
        :aria-label="t('general.add_to_cart.label')"
        role="button"
      >
        <div
          class="relative z1 h12 wfull flex flex-col select-none items-center justify-center text-center text-base text-white fw-600 !cursor-not-allowed"
          role="button" data-uniqid="26"
        >
          <span
            class="absolute inset-0 inline-block rd-md bg-black transition-all duration-150 -z1 n-active:bg-op40"
          />
          <svg
            class="mr-3 h-4 w-4 animate-spin text-white -ml-1"
            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path
              class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      </div>

      <button
        v-else
        class="flex-1" :aria-label="t('general.add_to_cart.label')" role="button"
        @click="emit('click')"
      >
        <div
          class="group relative z1 h12 wfull flex flex-col cursor-pointer select-none items-center justify-center text-center text-base text-white fw-600"
          role="button"
        >
          <span
            class="absolute inset-0 inline-block rd-md bg-black transition-all duration-150 -z1 group-hover:scale-102 n-active:bg-op40"
          />

          {{ t('general.add_to_cart.label') }}

          <div class="max-w-full text-center transition-all duration-200">
            <div flex="~ row wrap" class="relative items-center justify-center gap2">
              <template v-for="(label, index) in extraLabels" :key="index">
                <div
                  class="relative flex-row flex-wrap items-center justify-center whitespace-normal border-solid text-xs leading-0"
                  :class="{
                    'hidden xs:flex': index === 0,
                  }"
                >
                  <Icon :name="label.icon" class="lh1 me1 text-0.75rem" />
                  <span class="shrink-0 self-center text-white fw600">
                    {{ label.text }}
                  </span>
                </div>
                <div v-if="index === 0 && extraLabels.length > 1" class="h2 wpx bg-white/50 hidden xs:block" />
              </template>
            </div>
          </div>
        </div>
      </button>
    </div>
  </div>
</template>
